import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type { Story, ComicPanel } from '../types';

interface EarnedStory {
    story_id: string;
    story_title: string;
    story_content: string;
    comic_panels: ComicPanel[];
    difficulty_level: number;
    earned_at: string;
    is_viewed: boolean;
    game_session_score: number;
}

async function fetchEarnedStories() {
    const { data, error } = await supabase.rpc('get_user_earned_stories');
    if (error) throw new Error(error.message);
    console.log(data);
    return (data as any[])?.map(item => ({
      ...item,
      comic_panels: item.comic_panels as ComicPanel[] || []
    })) as EarnedStory[] || [];
}

async function fetchUnviewedStoriesCount() {
    const { data, error } = await supabase.rpc('get_unviewed_stories_count');
    if (error) throw new Error(error.message);
    return data as number;
}

async function markStoryAsViewed(storyId: string) {
    const { error } = await supabase.rpc('mark_story_as_viewed', { target_story_id: storyId });
    if (error) throw new Error(error.message);
}

export function useStories() {
    const queryClient = useQueryClient();

    const { data: earnedStories, isLoading: isLoadingStories, error: storiesError } = useQuery<EarnedStory[]>({
        queryKey: ['earned_stories'],
        queryFn: fetchEarnedStories
    });

    const { data: unviewedCount, isLoading: isLoadingCount, error: countError } = useQuery<number>({
        queryKey: ['unviewed_stories_count'],
        queryFn: fetchUnviewedStoriesCount
    });

    const markAsViewedMutation = useMutation({
        mutationFn: markStoryAsViewed,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['earned_stories'] });
            queryClient.invalidateQueries({ queryKey: ['unviewed_stories_count'] });
        },
    });

    const selectStory = (earnedStory: EarnedStory): Story => {
        if (!earnedStory.is_viewed) {
            markAsViewedMutation.mutate(earnedStory.story_id as string);
        }

        return {
            id: earnedStory.story_id,
            title: earnedStory.story_title,
            description: earnedStory.story_content,
            comic_panels: earnedStory.comic_panels,
            difficulty_level: earnedStory.difficulty_level,
            target_words: [],
            theme: 'general',
            estimated_duration: 5,
            created_at: earnedStory.earned_at,
            updated_at: earnedStory.earned_at,
        };
    };

    return {
        earnedStories: earnedStories || [],
        unviewedCount: unviewedCount || 0,
        isLoading: isLoadingStories || isLoadingCount,
        error: storiesError || countError,
        selectStory,
    };
}